import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  cancelMeasurement,
  canUseTool,
  selectActiveMeasurement,
  selectMeasurementStatus,
  setSelectedImageTools,
} from '@/state/slices/measurementSession';
import { canUseViewerTool } from '@/utils/measurementToolMapping';
import { CopilotViewerBridges } from '@/features/copilot';
import { useStudyReadiness } from '@/features/study-detail/studyReadiness';
import { ViewerGrid } from '../ViewerGrid';
import { ViewerControls } from '../ViewerControls';
import { ViewerFilterBar } from '../ViewerFilterBar';
import { useViewerContext } from '../ViewerContext';
import { usePlaybackContext } from '../PlaybackContext';
import { MANIPULATION_TOOLS } from '../hooks/useSharedToolState';
import { useSpectralDopplerAvailability } from '../tools/SpectralDoppler';
import { imageToolsOf } from '../tools/SpectralDoppler/useSpectralDopplerAvailability';
import { getNativeFps } from '../utils/frameRate';
import { useViewerLoading } from '../ViewerLoadingContext';
import { TopLoadingBar } from './TopLoadingBar';
import styles from './MultiViewerContainer.module.css';

interface MultiViewerContainerProps {
  isDetached: boolean;
  onToggleDetach: () => void;
  onDetachHover?: () => void;
  detachDisabled?: boolean;
}

export function MultiViewerContainer({
  isDetached,
  onToggleDetach,
  onDetachHover,
  detachDisabled,
}: MultiViewerContainerProps) {
  const {
    gridLayout,
    setGridLayout,
    handleViewportClick,
    handleToolChange,
    assignedDicoms,
    selectedViewportIndex,
    activeTool,
    deactivateTool,
    getPaneCineSpeed,
    setPaneCineSpeed,
    getPaneNativeFps,
    isInitialized,
    engineError,
    resetViewport,
    studyUuid,
    pinnedPanes,
    pinPane,
    unpinPane,
    placeDicomInPane,
    selectedViews,
    selectedGroupId,
  } = useViewerContext();

  const dispatch = useDispatch();
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const isArmed = useSelector(selectMeasurementStatus) === 'awaiting';

  // Doppler toolbar gating reads the currently-selected viewport's DICOM.
  const selectedDicom = useMemo(
    () => assignedDicoms[selectedViewportIndex] ?? assignedDicoms[0] ?? null,
    [assignedDicoms, selectedViewportIndex]
  );
  const spectralDopplerAvailability =
    useSpectralDopplerAvailability(selectedDicom);

  // Rows dim when their tool doesn't fit the selected image; an armed one disarms.
  const image = useMemo(
    () => imageToolsOf(spectralDopplerAvailability),
    [spectralDopplerAvailability]
  );
  const armedTool = activeMeasurement?.toolType;
  useEffect(() => {
    dispatch(setSelectedImageTools(image));
    if (armedTool && !canUseTool(armedTool, image)) {
      dispatch(cancelMeasurement());
    }
  }, [dispatch, image, armedTool]);

  // A toolbar tool the selected image can't take is turned off.
  useEffect(() => {
    if (activeTool && !canUseViewerTool(activeTool, image)) deactivateTool();
  }, [activeTool, image, deactivateTool]);

  // Frame-rate slider is anchored to the active clip's native FPS and edits
  // only the selected pane's speed (other panes keep their own rate). The
  // resolved (possibly video-derived) rate comes from the pane; fall back to
  // the DICOM fields until the pane reports it.
  const nativeFps =
    getPaneNativeFps(selectedViewportIndex) ||
    (selectedDicom ? getNativeFps(selectedDicom) : 0);
  const selectedCineSpeed = getPaneCineSpeed(selectedViewportIndex);
  const handleCineSpeedChange = useCallback(
    (multiplier: number) => setPaneCineSpeed(selectedViewportIndex, multiplier),
    [setPaneCineSpeed, selectedViewportIndex]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-deselect: clear the awaiting measurement (mirrors the Escape path)
  // before deactivating, otherwise useMeasurementToolActivation immediately
  // re-activates the tool while the Redux session is still 'awaiting'.
  const deselectActiveTool = useCallback(() => {
    dispatch(cancelMeasurement());
    deactivateTool();
  }, [dispatch, deactivateTool]);

  const { togglePlayPauseAll } = usePlaybackContext();

  // Global spacebar hotkey to play/pause all videos.
  // Uses capture phase so it intercepts Space before Cornerstone's keydown
  // listener or the native <video> element can consume/stop it.
  const spaceEnabled = assignedDicoms.length > 0 && isInitialized;
  useEffect(() => {
    if (!spaceEnabled) return undefined;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== ' ') return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable)
        return;
      e.preventDefault();
      e.stopPropagation();
      // Deactivate any active tool before toggling playback
      if (activeTool) {
        deactivateTool();
      }
      togglePlayPauseAll();
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [spaceEnabled, togglePlayPauseAll, activeTool, deactivateTool]);

  // Auto-deselect applies only to measurement/annotation tools — persistent
  // manipulation tools (W/L, Pan, Zoom) stay active on outside click/keypress.
  // An armed row disarms too, even before its tool activates (AP-803);
  // measurement labels handle arming themselves.
  useEffect(() => {
    const isMeasureTool = !!activeTool && !MANIPULATION_TOOLS.has(activeTool);
    if (!isMeasureTool && !isArmed) return undefined;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      if (containerRef.current?.contains(target)) return;
      if (target.closest?.('[data-measurement-label]')) return;
      deselectActiveTool();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeTool, isArmed, deselectActiveTool]);

  useEffect(() => {
    if (!activeTool || MANIPULATION_TOOLS.has(activeTool)) return undefined;
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable)
        return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      // Escape has a dedicated handler (handleCancelMeasurement in
      // useViewerHotkeys) that also cancels active manipulations and removes
      // any orphaned annotation — let it run instead of this generic path.
      if (
        ['Escape', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(
          e.key
        )
      )
        return;
      deselectActiveTool();
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [activeTool, deselectActiveTool]);

  // --- Study-readiness 'viewer' gate (AP-778) ---
  // Ready means: the Cornerstone engine is initialized AND every visible pane
  // has finished its FIRST load — media URLs resolved, viewport created,
  // content attached (`hasAnyPaneEverLoaded && !isAnyPaneLoading`; the
  // ever-loaded latch distinguishes "all done" from "none started yet").
  // Studies with nothing to show, and engine failures, count as ready — their
  // empty/error UI is the final state, and the reveal must not wait forever.
  const { isAnyPaneLoading, hasAnyPaneEverLoaded } = useViewerLoading();
  const { reportGate } = useStudyReadiness();
  const viewerGateReady =
    !!engineError ||
    (isInitialized &&
      (assignedDicoms.every((d) => d == null) ||
        (hasAnyPaneEverLoaded && !isAnyPaneLoading)));
  useEffect(() => {
    if (!studyUuid) return;
    reportGate(studyUuid, 'viewer', viewerGateReady);
  }, [studyUuid, viewerGateReady, reportGate]);

  if (!isInitialized && !engineError) {
    return (
      <div className={styles.multiViewerContainer}>
        <div className={styles.initSkeleton} />
      </div>
    );
  }

  if (engineError) {
    return (
      <div className={styles.multiViewerContainer}>
        <div className={styles.emptyState}>
          <div style={{ textAlign: 'center', color: '#ff4d4f' }}>
            <h3 style={{ margin: 0 }}>Failed to initialize viewer</h3>
            <p style={{ marginTop: '8px', fontSize: '14px' }}>
              {engineError.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no DICOMs
  if (assignedDicoms.length === 0) {
    return (
      <div className={styles.multiViewerContainer}>
        <div className={styles.emptyState}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 500 }}>
              Video Viewer
            </h2>
            <p style={{ fontSize: '14px', opacity: 0.6, marginTop: '8px' }}>
              No DICOM files available in this study
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasVisibleDicom = assignedDicoms.some((d) => d != null);
  const isFilterActive = selectedViews.length > 0 || selectedGroupId !== null;

  return (
    <div ref={containerRef} className={styles.multiViewerContainer}>
      <CopilotViewerBridges />
      <TopLoadingBar />
      <div className={styles.controlRow}>
        <ViewerFilterBar />
        <div className={styles.sectionDivider} />
        <ViewerControls
          nativeFps={nativeFps}
          cineSpeedMultiplier={selectedCineSpeed}
          onCineSpeedMultiplierChange={handleCineSpeedChange}
          isDetached={isDetached}
          onToggleDetach={onToggleDetach}
          onDetachHover={onDetachHover}
          detachDisabled={detachDisabled}
          activeTool={activeTool}
          onToolChange={handleToolChange}
          gridLayout={gridLayout}
          onGridLayoutChange={setGridLayout}
          onResetViewport={resetViewport}
          spectralDopplerAvailability={spectralDopplerAvailability}
          activeSimpsonType={activeMeasurement?.simpsonMeasurementType}
        />
      </div>
      {hasVisibleDicom ? (
        <ViewerGrid
          gridLayout={gridLayout}
          dicoms={assignedDicoms}
          onViewportClick={handleViewportClick}
          selectedViewportIndex={selectedViewportIndex}
          pinnedPanes={pinnedPanes}
          onPinPane={pinPane}
          onUnpinPane={unpinPane}
          onPlaceDicom={placeDicomInPane}
        />
      ) : (
        <div className={styles.emptyState}>
          {isFilterActive
            ? 'No views match the current filter'
            : 'No DICOM files available in this study'}
        </div>
      )}
    </div>
  );
}
